// src/utils/fetchLogger.ts

import { errorTracking } from './errorTracking';

type FetchLike = typeof fetch;

const ORIG_FETCH: FetchLike = window.fetch.bind(window);

// 送信用：できれば Firebase Analytics SDK からも送るが、確実性重視で sendBeacon を併用
function sendNetworkError(payload: Record<string, any>) {
  try {
    // Google Analyticsの通信エラーの場合は除外
    const isAnalyticsError = payload.path === '/g/collect' && payload.error_type === 'network_unknown';
    
    if (!isAnalyticsError) {
      // Firebase Analyticsにも送信
      errorTracking.logCustomError('Network request failed', payload);
    }

    // 任意：自前のCloud Functionsに集約するURL（PII注意）
    const url = import.meta.env.VITE_NET_ERROR_INGEST_URL || "";
    if (!url) return;
    
    const body = JSON.stringify({
      ...payload,
      t: new Date().toISOString(),
      href: window.location.href,
      ua: navigator.userAgent,
      release: import.meta.env.VITE_VERSION || 'unknown',
      env: import.meta.env.MODE,
    });
    
    if ("sendBeacon" in navigator) {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    } else {
      void fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body });
    }
  } catch (error) {
    // noop - ログ送信でエラーが起きても元のfetchの結果には影響しない
    console.warn('Failed to send network error log:', error);
  }
}

// エラー分類のヘルパー
function classifyError(error: any, status: number): string {
  if (status === 0) {
    // ネットワーク層でのエラー
    if (error?.message?.includes('CORS')) return 'cors_error';
    if (error?.message?.includes('Mixed Content')) return 'mixed_content_error';
    if (error?.message?.includes('certificate') || error?.message?.includes('SSL')) return 'certificate_error';
    if (error?.message?.includes('Network request failed') || error?.name === 'NetworkError') return 'network_disconnected';
    return 'network_unknown';
  }
  
  if (status >= 500) return 'server_error';
  if (status >= 400) return 'client_error';
  return 'unknown_error';
}

// fetch ラッパ
window.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
  const start = performance.now();
  let ok = false;
  let status = 0;
  let error: any = null;
  
  try {
    const res = await ORIG_FETCH(input, init);
    ok = res.ok;
    status = res.status;
    return res;
  } catch (e: any) {
    status = 0; // ネットワーク層での例外
    error = e;
    throw e;
  } finally {
    const end = performance.now();
    const duration = Math.round(end - start);
    
    // 失敗または 5xx/4xx をレポート（閾値は適宜）
    if (!ok || status >= 400) {
      const url = typeof input === "string" ? input : (input as any).url || String(input);
      
      // PII/機密URL対策：パスのみ抽出（クエリ・ドメインを落とすなど）
      const path = (() => {
        try {
          const u = new URL(url, window.location.origin);
          return u.pathname; // 必要なら u.origin を削除、クエリを落とす
        } catch {
          return url.replace(/[?#].*$/, ''); // クエリとハッシュを除去
        }
      })();

      const errorType = classifyError(error, status);
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

      const payload = {
        e: "network_error",
        error_type: errorType,
        method: (init?.method || "GET").toUpperCase(),
        status,
        path,
        dur_ms: duration,
        is_ios: isIOS,
        is_safari: isSafari,
        // エラーメッセージ（機密情報を含まない部分のみ）
        error_message: error?.message?.substring(0, 200) || null,
        error_name: error?.name || null,
        // リクエストヘッダーの有無（CORS関連の調査用）
        has_custom_headers: !!(init?.headers),
        cors_mode: init?.mode || 'cors',
        credentials: init?.credentials || 'same-origin',
        // メモリ情報（iPhone特有のメモリ不足問題の調査用）
        memory_mb: (performance as any).memory ? 
          Math.round((performance as any).memory.usedJSHeapSize / 1024 / 1024) : null,
        // ネットワーク接続状況
        connection_type: (navigator as any).connection?.effectiveType || 'unknown',
        is_online: navigator.onLine
      };

      sendNetworkError(payload);

      // iPhone特有のネットワークエラーの場合は特別にマーク
      if ((isIOS || isSafari) && (status === 0 || status >= 500)) {
        errorTracking.logCustomError('iOS network issue detected', {
          ...payload,
          special_case: 'ios_network_failure'
        });
      }
    }

    // 非常に遅いリクエストもログ（iPhone特有の性能問題の検出）
    if (duration > 10000 && ok) { // 10秒以上で成功した場合
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS) {
        const url = typeof input === "string" ? input : (input as any).url || String(input);
        const path = (() => {
          try {
            return new URL(url, window.location.origin).pathname;
          } catch {
            return url.replace(/[?#].*$/, '');
          }
        })();

        errorTracking.logCustomError('Slow network request on iOS', {
          e: "slow_network",
          method: (init?.method || "GET").toUpperCase(),
          path,
          dur_ms: duration,
          is_ios: true,
          memory_mb: (performance as any).memory ? 
            Math.round((performance as any).memory.usedJSHeapSize / 1024 / 1024) : null
        });
      }
    }
  }
}) as FetchLike;

// 開発環境での確認用
if (import.meta.env.MODE === 'development') {
  (window as any).testNetworkError = () => {
    fetch('https://nonexistent-domain-for-testing.invalid/api/test')
      .catch(() => null);
  };
}
